import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, GitCompareArrows, Loader2, ShieldQuestion, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { scoreSources } from "@/lib/intel/reliability";
import { detectContradictions, type Contradiction, type ContradictionReport } from "@/lib/intel/feeds.functions";

type Collectorish = { source: string; status?: string; summary?: string; latencyMs?: number; data?: unknown };

const SEV_TONE: Record<Contradiction["severity"], string> = {
  high: "border-danger/40 bg-danger/10 text-danger",
  medium: "border-amber/40 bg-amber/10 text-amber",
  low: "border-panel-border bg-panel/40 text-muted-foreground",
};

function ClaimCard({
  label, statement, source, date, winner,
}: { label: string; statement: string; source: string; date?: string; winner: boolean }) {
  return (
    <div className={`rounded border p-2.5 ${winner ? "border-success/40 bg-success/5" : "border-panel-border bg-background/40"}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-cyan">{label}</span>
        {winner && <span className="font-mono text-[9px] uppercase tracking-widest text-success">more authoritative</span>}
      </div>
      <div className="mt-1 text-sm">{statement}</div>
      <div className="mt-1 font-mono text-[10px] text-muted-foreground">
        {source}{date ? ` · ${date}` : ""}
      </div>
    </div>
  );
}

export function ContradictionPanel({
  scanRunId,
  collectors,
}: {
  scanRunId: string;
  collectors: Collectorish[] | undefined;
}) {
  const [report, setReport] = useState<ContradictionReport | null>(null);
  const run = useServerFn(detectContradictions);
  const m = useMutation({
    mutationFn: () => run({ data: { scanRunId } }),
    onSuccess: (r) => {
      setReport(r);
      toast.success(r.contradictions.length ? `${r.contradictions.length} contradiction(s) found` : "No contradictions detected");
    },
    onError: (e: any) => toast.error(e?.message ?? "Contradiction scan failed"),
  });

  // Deterministic, instant conflicts extracted from the collector payloads.
  const structural = useMemo(() => {
    const rows = scoreSources(collectors ?? []);
    const seen = new Set<string>();
    const out: Array<{ field: string; ours: string; theirs: string; a: string; b: string }> = [];
    for (const r of rows) {
      for (const c of r.contradictions) {
        const key = [r.source, c.source].sort().join("|") + "|" + c.field;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ field: c.field, ours: c.ours, theirs: c.theirs, a: r.source, b: c.source });
      }
    }
    return out.slice(0, 12);
  }, [collectors]);

  return (
    <section className="glass rounded-xl p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">
          <GitCompareArrows className="h-3 w-3" /> Contradiction detection
        </div>
        <button
          type="button"
          onClick={() => m.mutate()}
          disabled={m.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
        >
          {m.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
          {m.isPending ? "Cross-checking sources…" : report ? "Re-run AI check" : "Run AI contradiction check"}
        </button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Cross-checks self-reported claims (profiles, bios, press) against primary records (registries, filings, courts)
        and flags facts that cannot both be true.
      </p>

      {structural.length > 0 && (
        <div className="mt-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-amber">Field-level conflicts in collected data</div>
          <ul className="mt-2 space-y-1.5">
            {structural.map((c, i) => (
              <li key={i} className="rounded border border-amber/30 bg-amber/5 p-2 text-xs">
                <span className="font-mono text-[10px] uppercase tracking-widest text-amber">{c.field}</span>
                <div className="mt-1">
                  <span className="font-medium">“{c.ours}”</span> <span className="text-muted-foreground">({c.a})</span>
                  {" vs "}
                  <span className="font-medium">“{c.theirs}”</span> <span className="text-muted-foreground">({c.b})</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {report && (
        <div className="mt-4 space-y-3">
          <div className="rounded border border-panel-border bg-background/40 p-2.5 text-xs text-muted-foreground">
            {report.summary}
          </div>
          {report.contradictions.length === 0 && (
            <div className="inline-flex items-center gap-2 text-xs text-success">
              <ShieldQuestion className="h-3.5 w-3.5" /> Sources agree on every fact checked.
            </div>
          )}
          {report.contradictions.map((c, i) => (
            <article key={i} className="rounded-lg border border-panel-border bg-panel/30 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${SEV_TONE[c.severity]}`}>
                  <AlertTriangle className="h-3 w-3" /> {c.severity}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{c.field}</span>
                <span className="ml-auto font-mono text-[10px] text-muted-foreground">confidence {c.confidence}%</span>
              </div>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <ClaimCard label="Claim A" statement={c.claimA.statement} source={c.claimA.source} date={c.claimA.date} winner={c.likelyCorrect === "a"} />
                <ClaimCard label="Claim B" statement={c.claimB.statement} source={c.claimB.source} date={c.claimB.date} winner={c.likelyCorrect === "b"} />
              </div>
              <p className="mt-2 text-xs leading-relaxed">{c.explanation}</p>
              {c.resolution && (
                <p className="mt-1.5 text-[11px] text-muted-foreground">
                  <span className="font-mono uppercase tracking-widest text-cyan">Resolve:</span> {c.resolution}
                </p>
              )}
            </article>
          ))}
          {report.citations.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {report.citations.slice(0, 8).map((u) => (
                <a key={u} href={u} target="_blank" rel="noreferrer"
                  className="max-w-[220px] truncate rounded border border-panel-border bg-background/40 px-2 py-0.5 font-mono text-[10px] text-muted-foreground hover:text-cyan">
                  {u.replace(/^https?:\/\//, "")}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
